// shared edge/label geometry used by the canvas edge renderer (StrainEdge),
// the auto-layout pass (layout.ts), and the visual-language lab pages.
// one implementation so routing and layout agree about label sizes and
// collision boxes.

export type Point = { x: number; y: number };
export type Rect = { left: number; right: number; top: number; bottom: number };
export type MeasuredRect = Rect & { width: number; height: number; cx: number; cy: number };

export function clamp(value: number, min: number, max: number): number {
  if (min > max) return value;
  return Math.min(max, Math.max(min, value));
}

export function expandedRect(rect: Rect, amount: number): Rect {
  return {
    left: rect.left - amount,
    right: rect.right + amount,
    top: rect.top - amount,
    bottom: rect.bottom + amount,
  };
}

export function rectOverlapArea(a: Rect, b: Rect): number {
  const width = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const height = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return width * height;
}

// label box estimate for an edge chip; `undercuts` widens the chip for the
// trailing counter.
export function estimateEdgeLabelSize(label: string, undercuts = 0): { w: number; h: number } {
  return {
    w: Math.min(220, Math.max(76, 34 + label.length * 6.2 + (undercuts > 0 ? 22 : 0))),
    h: 24,
  };
}

export function roundedPolylinePath(points: Point[], radius = 14): string {
  if (points.length < 2) return "";

  const [start, ...rest] = points;
  let path = `M ${start.x} ${start.y}`;

  rest.forEach((point, index) => {
    const next = rest[index + 1];
    const prev = points[index];
    if (!next) {
      path += ` L ${point.x} ${point.y}`;
      return;
    }

    const prevDistance = Math.hypot(point.x - prev.x, point.y - prev.y);
    const nextDistance = Math.hypot(next.x - point.x, next.y - point.y);
    const cornerRadius = Math.min(radius, prevDistance / 2, nextDistance / 2);
    if (cornerRadius <= 0) {
      path += ` L ${point.x} ${point.y}`;
      return;
    }

    const before = {
      x: point.x + ((prev.x - point.x) / prevDistance) * cornerRadius,
      y: point.y + ((prev.y - point.y) / prevDistance) * cornerRadius,
    };
    const after = {
      x: point.x + ((next.x - point.x) / nextDistance) * cornerRadius,
      y: point.y + ((next.y - point.y) / nextDistance) * cornerRadius,
    };

    path += ` L ${before.x} ${before.y} Q ${point.x} ${point.y} ${after.x} ${after.y}`;
  });

  return path;
}

export function polylineLength(points: Point[]): number {
  let length = 0;
  for (let i = 1; i < points.length; i += 1) {
    length += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return length;
}

export function pointAtPolylineFraction(points: Point[], fraction: number): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];
  const total = polylineLength(points);
  const target = total * fraction;
  let walked = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const segment = Math.hypot(b.x - a.x, b.y - a.y);
    if (walked + segment >= target) {
      const t = segment === 0 ? 0 : (target - walked) / segment;
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
    walked += segment;
  }
  return points[points.length - 1];
}

export function segmentIntersectsRect(a: Point, b: Point, rect: Rect): boolean {
  const minX = Math.min(a.x, b.x);
  const maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxY = Math.max(a.y, b.y);
  if (maxX < rect.left || minX > rect.right || maxY < rect.top || minY > rect.bottom) return false;

  if (Math.abs(a.y - b.y) < 0.01) {
    return a.y >= rect.top && a.y <= rect.bottom && maxX >= rect.left && minX <= rect.right;
  }
  if (Math.abs(a.x - b.x) < 0.01) {
    return a.x >= rect.left && a.x <= rect.right && maxY >= rect.top && minY <= rect.bottom;
  }

  return true;
}

export interface LabelPlacement {
  point: Point;
  rect: Rect;
  overlap: number;
}

// slide a label along/around its edge until it stops covering node cards.
// candidates fan out along the edge direction and perpendicular to it; the
// first zero-overlap candidate wins, otherwise the least-covered one.
export function chooseLabelPoint({
  baseX, baseY, sx, sy, tx, ty, labelW, labelH, nodeRects, clearance = 18,
}: {
  baseX: number;
  baseY: number;
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  labelW: number;
  labelH: number;
  nodeRects: Rect[];
  clearance?: number;
}): LabelPlacement {
  const dx = tx - sx;
  const dy = ty - sy;
  const len = Math.max(1, Math.hypot(dx, dy));
  const ux = dx / len;
  const uy = dy / len;
  const nx = -uy;
  const ny = ux;

  const perpendicular = labelH + clearance + 8;
  const widePerpendicular = labelH + clearance + 34;
  const farPerpendicular = labelH + clearance + 64;
  const along = Math.min(108, Math.max(50, len * 0.22));
  const alongOffsets = [0, along, -along, along * 1.8, -along * 1.8];
  const perpendicularOffsets = [0, perpendicular, -perpendicular, widePerpendicular, -widePerpendicular, farPerpendicular, -farPerpendicular];
  const candidates: Point[] = [{ x: baseX, y: baseY }];
  for (const alongOffset of alongOffsets) {
    for (const perpendicularOffset of perpendicularOffsets) {
      if (alongOffset === 0 && perpendicularOffset === 0) continue;
      candidates.push({
        x: baseX + ux * alongOffset + nx * perpendicularOffset,
        y: baseY + uy * alongOffset + ny * perpendicularOffset,
      });
    }
  }

  const rectFor = (point: Point): Rect => ({
    left: point.x - labelW / 2,
    right: point.x + labelW / 2,
    top: point.y - labelH / 2,
    bottom: point.y + labelH / 2,
  });

  let best: LabelPlacement & { score: number } = {
    point: candidates[0],
    rect: rectFor(candidates[0]),
    overlap: Number.POSITIVE_INFINITY,
    score: Number.POSITIVE_INFINITY,
  };

  for (const point of candidates) {
    const rect = rectFor(point);
    const overlap = nodeRects.reduce((sum, nodeRect) => (
      sum + rectOverlapArea(rect, expandedRect(nodeRect, clearance))
    ), 0);
    const distance = Math.hypot(point.x - baseX, point.y - baseY);
    const score = overlap * 10000 + distance;
    if (score < best.score) {
      best = { point, rect, overlap, score };
      if (overlap === 0 && distance > 0) break;
    }
  }

  return { point: best.point, rect: best.rect, overlap: best.overlap };
}
